/**
 * Рендер сообщения так, как его увидит подписчик. 09-blockers-closed.md §9.2.
 *
 * Компонент ничего не знает о том, откуда взялись пропсы: в фазе 1 их источник — модель
 * рендера, в фазе 2 вторым источником станет редактор, и переписывать здесь будет нечего
 * (05-ui.md §5). Всё остальное он выводит сам:
 *   entitiesToRender = mode === 'expected' ? expectedServerEntities(canonical) : canonical.entities
 *
 * Разница между двумя режимами и есть то, что добавит Telegram сам — самый наглядный способ
 * показать §3 контракта человеку, который жмёт «Утвердить».
 */

import { buildTree, type DraftContent, type PreviewNode } from '@smm/contracts';
import type { DocumentRef, MediaRef } from '@smm/contracts/schemas';
import type { JSX } from 'react';
import { ChannelFooter, ChannelHeader } from './ChannelChrome.tsx';
import { DocumentBlock } from './DocumentBlock.tsx';
import { PreviewNodes } from './entityNode.tsx';
import { MediaBlock } from './MediaBlock.tsx';
import styles from './MessagePreview.module.css';
import type { DiffMarks } from './marks.tsx';
import { entitiesForMode, type PreviewMode, type PreviewTheme } from './validation.ts';

export interface MessagePreviewProps {
  /**
   * Канонический контент версии: cv, method, text, entities, link_preview_options, media.
   *
   * Тип — `DraftContent`, а не `CanonicalDraft` из 09 §9.2: бренд `canonical` ставит только
   * `canonicalize()`, а модель рендера приезжает по HTTP и разбирается `RenderModelSchema`,
   * где `canonical` объявлен `DraftContentSchema`. Взять бренд здесь можно было бы только
   * приведением типа, которое правило R12 стража границ запрещает — и правильно делает:
   * оно бы утверждало каноничность, которую никто не проверял. Проверяет её validateDraft().
   */
  readonly canonical: DraftContent;
  /** Непусто ⟺ canonical.media !== null. Метод на это больше не отвечает. */
  readonly mediaRef: MediaRef | null;
  /**
   * Адрес байтов документа (§9.2). Сам блок документа рисуется по `canonical.document` —
   * адрес только делает его ссылкой «открыть файл». Не передан — блок без действия.
   */
  readonly documentRef?: DocumentRef | null;
  readonly mode: PreviewMode;
  /** Тема ПРЕВЬЮ, не приложения: подписчик читает канал в своей (ui-design.md §6.2). */
  readonly theme: PreviewTheme;
  /** Имя канала для обвязки-иллюстрации. */
  readonly brand?: string;
  /**
   * Режим «дифф»: диапазоны текста, помеченные как убранные или добавленные. Именно так
   * сравнение версий показывает две версии — тем же компонентом, что и режим чтения, а не
   * вторым рендером поста (решение заказчика 26.08.2026).
   */
  readonly marks?: DiffMarks | null;
}

export function MessagePreview({
  canonical,
  mediaRef,
  documentRef = null,
  mode,
  theme,
  brand = 'канал',
  marks = null,
}: MessagePreviewProps): JSX.Element {
  const entities = entitiesForMode(canonical, mode);

  // Дерево строится в try: buildTree бросает на сломанной канонической форме, и падение
  // всего экрана оставило бы человека без единственного, по чему он принимает решение.
  // Причина падения при этом не прячется — validateDraft() поднимает её как блокирующую.
  let nodes: readonly PreviewNode[];
  let broken = false;
  try {
    nodes = buildTree(canonical.text, entities);
  } catch {
    nodes = [{ kind: 'text', text: canonical.text }];
    broken = true;
  }

  // Не «подпись»: у поста с картинкой это тот же текст поста, отдельной сущности «подпись»
  // не существует с миграции 0013.
  const body = (
    <div className={styles['body']} data-testid="tg-body">
      <PreviewNodes nodes={nodes} marks={marks} />
    </div>
  );

  const media =
    mediaRef === null || canonical.media === null ? null : (
      <MediaBlock
        mediaId={mediaRef.media_id}
        hasSpoiler={canonical.media.has_spoiler}
        width={mediaRef.width}
        height={mediaRef.height}
      />
    );

  // Адрес берётся, только если он про ЭТОТ документ: `document_ref` повторяет имя и размер
  // дескриптора, и расхождение значит, что ссылка открыла бы не тот файл, который утверждают.
  // Отсутствующее поле — содержание старше 19.09.2026 — то же, что `null`.
  const doc = canonical.document ?? null;
  const docRef =
    doc !== null &&
    documentRef !== null &&
    documentRef.file_name === doc.file_name &&
    documentRef.bytes === doc.bytes
      ? documentRef.media_id
      : null;
  const document = doc === null ? null : <DocumentBlock document={doc} mediaId={docRef} />;

  return (
    <div className={theme === 'dark' ? 'tg dark' : 'tg'} data-testid="tg-root">
      <ChannelHeader brand={brand} />
      {/* Картинка ВСЕГДА первая: блок photo идёт первым в richFromCanonical (rich.ts), и это
          решение Ильи 06.09.2026, а не настройка. `show_caption_above_media` остался в
          подписываемых байтах, но порядок он не меняет. Панель существует ради обещания «1:1»,
          поэтому вариант вёрстки, которого провод не породит, здесь не рисуется вовсе.
          Документ (PDF, 19.09.2026) — ВСЕГДА последний блок, по тому же правилу rich.ts. */}
      <div className="tg-bubble">
        {media}
        {body}
        {document}
        <ChannelFooter />
      </div>
      {broken ? (
        <p className={styles['renderFail']}>
          Разметку не удалось разобрать: сущности пересекаются частично. Показан голый текст —
          решение по такому черновику принимать нельзя.
        </p>
      ) : null}
    </div>
  );
}
